import { Button, notification } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { useEffect } from 'react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const DISMISS_KEY = 'dtable_install_dismissed_until';
const DISMISS_DAYS = 30;

/**
 * PWA install prompt (§6.5). Listens for beforeinstallprompt (Chrome/Edge on
 * supported platforms) and surfaces a corner-of-screen notification with an
 * "Install app" button. Silently dismissed for 30 days if the user clicks away.
 */
export function InstallPrompt() {
  useEffect(() => {
    const dismissedUntil = Number(localStorage.getItem(DISMISS_KEY) || 0);
    if (dismissedUntil > Date.now()) return;

    let deferredPrompt: BeforeInstallPromptEvent | null = null;

    const handler = (e: Event) => {
      e.preventDefault();
      deferredPrompt = e as BeforeInstallPromptEvent;

      notification.info({
        key: 'install-prompt',
        message: 'Install D Table',
        description:
          'Install the app for faster access and offline clock-in support.',
        placement: 'bottomRight',
        duration: 0,
        btn: (
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            onClick={async () => {
              if (!deferredPrompt) return;
              notification.destroy('install-prompt');
              await deferredPrompt.prompt();
              const { outcome } = await deferredPrompt.userChoice;
              if (outcome === 'dismissed') {
                localStorage.setItem(
                  DISMISS_KEY,
                  String(Date.now() + DISMISS_DAYS * 24 * 60 * 60 * 1000),
                );
              }
              deferredPrompt = null;
            }}
          >
            Install
          </Button>
        ),
        onClose: () => {
          localStorage.setItem(
            DISMISS_KEY,
            String(Date.now() + DISMISS_DAYS * 24 * 60 * 60 * 1000),
          );
        },
      });
    };

    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  return null;
}
